import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Grid,
  MenuItem,
  Typography,
  Divider,
  alpha,
  LinearProgress,
  Stack,
  Alert,
  Chip,
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
} from "@mui/material";
import {
  DirectionsCar,
  TwoWheeler,
  Save,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Refresh,
  Delete,
  Close,
  Add,
  Edit,
  Person,
} from "@mui/icons-material";
import MDButton from "shared/components/MDButton";
import MDBox from "shared/components/MDBox";
import logoImage from "../../../../../assets/images/logo horinzontal.png";

const vehicleTypes = {
  1: "Vehículo",
  2: "Moto",
};

// Expresiones regulares para validaciones
const PLATE_REGEX = /^[A-Z]{3}\d{3}$|^[A-Z]{3}\d{2}[A-Z]$/;
const CEDULA_REGEX = /^\d+$/;
const NAME_REGEX = /^[A-Za-záéíóúÁÉÍÓÚñÑ\s]+$/;

const UsuarioDialog = ({
  open,
  onClose,
  onConfirm,
  dialogType,
  selectedUsuario,
  currentUser,
  isSubmitting,
  submitStatus,
  statusMessage,
}) => {
  const [formData, setFormData] = useState({
    plate: "",
    start_date: "",
    end_date: "",
    idvehicletype: 1,
    cedula: "",
    name: "",
    lastname: "",
    residential: "",
    UserGoper: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Resetear formulario cuando se abre el diálogo
  useEffect(() => {
    if (open) {
      if (dialogType === "add") {
        setFormData({
          plate: "",
          start_date: "",
          end_date: "",
          idvehicletype: 1,
          cedula: "",
          name: "",
          lastname: "",
          residential: "",
          UserGoper: currentUser?.username || "",
        });
        setErrors({});
        setTouched({});
      } else if (dialogType === "edit" && selectedUsuario) {
        const formatDateForInput = (dateStr) => {
          if (!dateStr) return "";
          const date = new Date(dateStr);
          return date.toISOString().slice(0, 16);
        };
        setFormData({
          plate: selectedUsuario.plate || "",
          start_date: formatDateForInput(selectedUsuario.start_date),
          end_date: formatDateForInput(selectedUsuario.end_date),
          idvehicletype: selectedUsuario.idvehicletype || 1,
          cedula: selectedUsuario.cedula || "",
          name: selectedUsuario.name || "",
          lastname: selectedUsuario.lastname || "",
          residential: selectedUsuario.residential || "",
          UserGoper: selectedUsuario.UserGoper || currentUser?.username || "",
        });
        setErrors({});
        setTouched({});
      }
    }
  }, [open, dialogType, selectedUsuario, currentUser]);

  // Validación de placa: AAA123 o AAA12H
  const validatePlate = (value) => {
    if (!value) return "La placa es requerida";
    const upperValue = value.toUpperCase();
    if (!PLATE_REGEX.test(upperValue)) {
      return "Formato inválido. Use AAA123 o AAA12H";
    }
    return "";
  };

  // Validación de cédula: solo números
  const validateCedula = (value) => {
    if (!value) return "La cédula es requerida";
    if (!CEDULA_REGEX.test(value)) {
      return "La cédula debe contener solo números";
    }
    return "";
  };

  // Validación de nombre: solo letras y espacios
  const validateName = (value) => {
    if (!value) return "El nombre es requerido";
    if (!NAME_REGEX.test(value)) {
      return "El nombre solo debe contener letras";
    }
    return "";
  };

  // Validación de apellido: solo letras y espacios
  const validateLastname = (value) => {
    if (!value) return "El apellido es requerido";
    if (!NAME_REGEX.test(value)) {
      return "El apellido solo debe contener letras";
    }
    return "";
  };

  const validateField = (field, value) => {
    let error = "";

    switch (field) {
      case "plate":
        error = validatePlate(value);
        break;
      case "cedula":
        error = validateCedula(value);
        break;
      case "name":
        error = validateName(value);
        break;
      case "lastname":
        error = validateLastname(value);
        break;
      case "residential":
        if (!value) error = "La residencial es requerida";
        break;
      case "start_date":
        if (!value) error = "La fecha de inicio es requerida";
        break;
      case "end_date":
        if (!value) {
          error = "La fecha de fin es requerida";
        } else if (formData.start_date && new Date(value) <= new Date(formData.start_date)) {
          error = "La fecha de fin debe ser mayor a la fecha de inicio";
        }
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));

    return !error;
  };

  const isFieldError = (field) => {
    return touched[field] && errors[field];
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Convertir placa a mayúsculas
    if (name === "plate") {
      processedValue = value.toUpperCase();
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    if (name === "start_date" && formData.end_date) {
      validateField("end_date", formData.end_date);
    }

    validateField(name, processedValue);
  };

  const validateForm = () => {
    const fields = ["plate", "cedula", "name", "lastname", "residential", "start_date", "end_date"];
    let isValid = true;

    fields.forEach((field) => {
      const fieldValid = validateField(field, formData[field]);
      if (!fieldValid) isValid = false;
      setTouched((prev) => ({
        ...prev,
        [field]: true,
      }));
    });

    return isValid;
  };

  const handleConfirm = () => {
    if (dialogType === "delete") {
      onConfirm(null);
      return;
    }

    if (!validateForm()) {
      return;
    }

    onConfirm(formData);
  };

  const isFormComplete = () => {
    const requiredFields = [
      "plate",
      "cedula",
      "name",
      "lastname",
      "residential",
      "start_date",
      "end_date",
    ];
    return requiredFields.every((field) => formData[field] && formData[field].trim() !== "");
  };

  const isEndDateValid = () => {
    if (!formData.start_date || !formData.end_date) return false;
    return new Date(formData.end_date) > new Date(formData.start_date);
  };

  // Renderizar contenido según el tipo de diálogo
  const renderContent = () => {
    // --- DIÁLOGO DE ELIMINACIÓN MEJORADO ---
    if (dialogType === "delete") {
      return (
        <Box>
          {/* Header con gradiente rojo */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #c62828 0%, #d32f2f 100%)",
              p: 7,
              pb: 4,
              textAlign: "center",
              position: "relative",
              mx: -3,
              mt: -3,
              mb: 3,
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
            }}
          >
            <Box
              component="img"
              src={logoImage}
              alt="GoPass"
              sx={{
                height: 60,
                width: "auto",
                objectFit: "contain",
                mb: 2,
                filter: "brightness(0) invert(1)",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />

            <Typography
              variant="h5"
              fontWeight="bold"
              color="rgba(255,255,255,0.85)"
              sx={{ mt: 2 }}
            >
              Eliminar Usuario
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.85)">
              Confirme la eliminación del usuario
            </Typography>
          </Box>

          {/* Contenido de confirmación */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid #ffebee",
              backgroundColor: "#fff5f5",
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Person sx={{ color: "#c62828" }} />
              <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                USUARIO A ELIMINAR
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Placa
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {selectedUsuario?.plate || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Tipo
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {selectedUsuario?.idvehicletype === 1 ? "Vehículo" : "Moto"}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Nombre
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {selectedUsuario?.name || "N/A"} {selectedUsuario?.lastname || ""}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Cédula
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {selectedUsuario?.cedula || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Residencial
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {selectedUsuario?.residential || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Creado por
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {selectedUsuario?.UserGoper || "N/A"}
                </Typography>
              </Grid>
            </Grid>

            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: "8px",
                backgroundColor: "rgba(198, 40, 40, 0.08)",
                border: "1px solid rgba(198, 40, 40, 0.2)",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Warning sx={{ color: "#c62828", fontSize: 20 }} />
              <Typography variant="body2" color="#c62828" fontWeight="medium">
                Esta acción no se puede deshacer. El usuario será eliminado permanentemente.
              </Typography>
            </Box>
          </Paper>
        </Box>
      );
    }

    // --- DIÁLOGO DE CREACIÓN/EDICIÓN ---
    return (
      <Stack spacing={3}>
        {submitStatus && (
          <Alert
            severity={submitStatus === "success" ? "success" : "error"}
            sx={{ borderRadius: "8px" }}
          >
            <Typography variant="body2" fontWeight="medium">
              {statusMessage}
            </Typography>
          </Alert>
        )}

        <MDBox
          sx={{
            p: 3,
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Grid container spacing={3}>
            {/* Placa */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Placa *"
                name="plate"
                value={formData.plate}
                onChange={handleFormChange}
                error={isFieldError("plate")}
                helperText={isFieldError("plate") && errors.plate}
                disabled={isSubmitting}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DirectionsCar sx={{ color: "#5c6bc0" }} />
                    </InputAdornment>
                  ),
                }}
                placeholder="Ej: ABC123 o ABC12H"
                sx={{
                  "& .MuiInputBase-root": { height: "56px", fontSize: "1rem" },
                }}
              />
            </Grid>

            {/* Cédula */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cédula *"
                name="cedula"
                value={formData.cedula}
                onChange={handleFormChange}
                error={isFieldError("cedula")}
                helperText={isFieldError("cedula") && errors.cedula}
                disabled={isSubmitting}
                variant="outlined"
                placeholder="Ej: 1018453472"
                sx={{
                  "& .MuiInputBase-root": { height: "56px", fontSize: "1rem" },
                }}
              />
            </Grid>

            {/* Nombre */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre *"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                error={isFieldError("name")}
                helperText={isFieldError("name") && errors.name}
                disabled={isSubmitting}
                variant="outlined"
                placeholder="Ej: Juan"
                sx={{
                  "& .MuiInputBase-root": { height: "56px", fontSize: "1rem" },
                }}
              />
            </Grid>

            {/* Apellido */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Apellido *"
                name="lastname"
                value={formData.lastname}
                onChange={handleFormChange}
                error={isFieldError("lastname")}
                helperText={isFieldError("lastname") && errors.lastname}
                disabled={isSubmitting}
                variant="outlined"
                placeholder="Ej: Gomez"
                sx={{
                  "& .MuiInputBase-root": { height: "56px", fontSize: "1rem" },
                }}
              />
            </Grid>

            {/* Residencial */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Residencial *"
                name="residential"
                value={formData.residential}
                onChange={handleFormChange}
                error={isFieldError("residential")}
                helperText={isFieldError("residential") && errors.residential}
                disabled={isSubmitting}
                variant="outlined"
                placeholder="Ej: 16-502"
                sx={{
                  "& .MuiInputBase-root": { height: "56px", fontSize: "1rem" },
                }}
              />
            </Grid>

            {/* Tipo Vehículo */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Tipo Vehículo *"
                name="idvehicletype"
                value={formData.idvehicletype}
                onChange={handleFormChange}
                disabled={isSubmitting}
                variant="outlined"
                sx={{
                  "& .MuiInputBase-root": { height: "56px", fontSize: "1rem" },
                }}
              >
                <MenuItem value={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <DirectionsCar sx={{ color: "#5c6bc0" }} />
                    Vehículo
                  </Box>
                </MenuItem>
                <MenuItem value={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <TwoWheeler sx={{ color: "#ff9800" }} />
                    Moto
                  </Box>
                </MenuItem>
              </TextField>
            </Grid>

            {/* Fecha Inicio */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha Inicio *"
                name="start_date"
                type="datetime-local"
                value={formData.start_date}
                onChange={handleFormChange}
                error={isFieldError("start_date")}
                helperText={isFieldError("start_date") && errors.start_date}
                disabled={isSubmitting}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiInputBase-root": { height: "56px", fontSize: "1rem" },
                }}
              />
            </Grid>

            {/* Fecha Fin */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha Fin *"
                name="end_date"
                type="datetime-local"
                value={formData.end_date}
                onChange={handleFormChange}
                error={isFieldError("end_date")}
                helperText={isFieldError("end_date") && errors.end_date}
                disabled={isSubmitting || !formData.start_date}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "56px",
                    fontSize: "1rem",
                    backgroundColor: !formData.start_date ? alpha("#f5f5f5", 0.5) : "transparent",
                  },
                }}
              />
            </Grid>
          </Grid>
        </MDBox>

        {/* Resumen del Usuario */}
        <MDBox
          sx={{
            p: 3,
            borderRadius: "12px",
            border: "1px solid",
            borderColor: alpha("#5c6bc0", 0.3),
            backgroundColor: alpha("#5c6bc0", 0.02),
          }}
        >
          <Typography variant="h6" fontWeight="600" color="text.primary" gutterBottom>
            RESUMEN DEL USUARIO
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.7rem", fontWeight: 500 }}
              >
                PLACA
              </Typography>
              <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }} noWrap>
                {formData.plate || "—"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.7rem", fontWeight: 500 }}
              >
                CÉDULA
              </Typography>
              <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }} noWrap>
                {formData.cedula || "—"}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.7rem", fontWeight: 500 }}
              >
                NOMBRE COMPLETO
              </Typography>
              <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }} noWrap>
                {formData.name && formData.lastname ? `${formData.name} ${formData.lastname}` : "—"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.7rem", fontWeight: 500 }}
              >
                RESIDENCIAL
              </Typography>
              <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }} noWrap>
                {formData.residential || "—"}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.7rem", fontWeight: 500 }}
              >
                TIPO VEHÍCULO
              </Typography>
              <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.9rem" }}>
                {vehicleTypes[formData.idvehicletype] || "—"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.7rem", fontWeight: 500 }}
              >
                FECHAS
              </Typography>
              <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.9rem" }}>
                {formData.start_date && formData.end_date ? (
                  isEndDateValid() ? (
                    <Chip
                      label="✅ Fechas válidas"
                      size="small"
                      color="success"
                      sx={{ fontSize: "0.65rem", height: "20px" }}
                    />
                  ) : (
                    <Chip
                      label="⚠️ Fechas inválidas"
                      size="small"
                      color="error"
                      sx={{ fontSize: "0.65rem", height: "20px" }}
                    />
                  )
                ) : (
                  "—"
                )}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.7rem", fontWeight: 500 }}
              >
                CREADO POR
              </Typography>
              <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.9rem" }}>
                {formData.UserGoper || currentUser?.username || "—"}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.7rem", fontWeight: 500 }}
              >
                COMPLETITUD DEL FORMULARIO
              </Typography>
              <Typography variant="caption" fontWeight="600" sx={{ fontSize: "0.7rem" }}>
                {Object.values(formData).filter((v) => v && v.toString().trim() !== "").length}
                /7 campos
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={
                (Object.values(formData).filter((v) => v && v.toString().trim() !== "").length /
                  7) *
                100
              }
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: alpha("#5c6bc0", 0.1),
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "#5c6bc0",
                  borderRadius: 3,
                },
              }}
            />
          </Box>

          {isFormComplete() && isEndDateValid() ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: "8px",
                backgroundColor: alpha("#4caf50", 0.05),
                border: "1px dashed",
                borderColor: alpha("#4caf50", 0.3),
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CheckCircle sx={{ color: "#4caf50", fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                Todos los campos requeridos están completos y las fechas son válidas.
              </Typography>
            </Box>
          ) : isFormComplete() && !isEndDateValid() && formData.start_date && formData.end_date ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: "8px",
                backgroundColor: alpha("#d32f2f", 0.05),
                border: "1px dashed",
                borderColor: alpha("#d32f2f", 0.3),
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ErrorIcon sx={{ color: "#d32f2f", fontSize: 18 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem", color: "#d32f2f" }}
              >
                La fecha de fin debe ser mayor a la fecha de inicio
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                p: 1.5,
                borderRadius: "8px",
                backgroundColor: alpha("#ff9800", 0.05),
                border: "1px dashed",
                borderColor: alpha("#ff9800", 0.3),
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Warning sx={{ color: "#ff9800", fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                Complete todos los campos requeridos y asegúrese de que las fechas sean válidas
              </Typography>
            </Box>
          )}
        </MDBox>
      </Stack>
    );
  };

  // Renderizar footer según el tipo de diálogo y estado
  const renderFooter = () => {
    if (dialogType === "delete") {
      return (
        <>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
          >
            Cancelar
          </MDButton>
          <MDButton
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={isSubmitting}
            startIcon={<Delete />}
            sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
          >
            {isSubmitting ? "Eliminando..." : "Eliminar"}
          </MDButton>
        </>
      );
    }

    if (submitStatus === "success") {
      return (
        <MDButton
          variant="contained"
          color="success"
          onClick={onClose}
          startIcon={<CheckCircle />}
          sx={{ px: 4, minWidth: 150, borderRadius: "8px", height: "48px" }}
        >
          Aceptar
        </MDButton>
      );
    }

    if (submitStatus === "error") {
      return (
        <>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={onClose}
            sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
          >
            Cerrar
          </MDButton>
          <MDButton
            variant="contained"
            color="warning"
            onClick={() => {
              onClose();
            }}
            startIcon={<Refresh />}
            sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
          >
            Cancelar
          </MDButton>
        </>
      );
    }

    return (
      <>
        <MDButton
          variant="outlined"
          color="secondary"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
        >
          Cancelar
        </MDButton>
        <MDButton
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={isSubmitting || !isFormComplete() || !isEndDateValid()}
          startIcon={<Save />}
          sx={{ px: 4, minWidth: 200, borderRadius: "8px", fontWeight: "bold", height: "48px" }}
        >
          {isSubmitting
            ? dialogType === "add"
              ? "Agregando..."
              : "Actualizando..."
            : dialogType === "add"
            ? "AGREGAR USUARIO"
            : "ACTUALIZAR USUARIO"}
        </MDButton>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
          overflow: "hidden",
        },
      }}
    >
      {dialogType !== "delete" && (
        <DialogTitle
          sx={{
            pb: 2,
            pt: 3,
            px: 3,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Box display="flex" alignItems="center">
            {isSubmitting ? (
              <CircularProgress size={28} sx={{ mr: 2, color: "primary.main" }} />
            ) : submitStatus === "success" ? (
              <CheckCircle sx={{ mr: 2, fontSize: "1.75rem", color: "success.main" }} />
            ) : submitStatus === "error" ? (
              <ErrorIcon sx={{ mr: 2, fontSize: "1.75rem", color: "error.main" }} />
            ) : dialogType === "add" ? (
              <Add sx={{ mr: 2, fontSize: "1.75rem", color: "primary.main" }} />
            ) : (
              <Edit sx={{ mr: 2, fontSize: "1.75rem", color: "primary.main" }} />
            )}
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {dialogType === "add" && "Agregar Nuevo Usuario"}
                {dialogType === "edit" && "Editar Usuario"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {dialogType === "add" && "Complete todos los campos para agregar un nuevo usuario"}
                {dialogType === "edit" && "Actualice los datos del usuario"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
      )}

      <DialogContent sx={{ pt: dialogType === "delete" ? 0 : 3, px: 3 }}>
        {renderContent()}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, borderTop: 1, borderColor: "divider", gap: 1 }}>
        {renderFooter()}
      </DialogActions>
    </Dialog>
  );
};

UsuarioDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  dialogType: PropTypes.string,
  selectedUsuario: PropTypes.object,
  currentUser: PropTypes.object,
  isSubmitting: PropTypes.bool,
  submitStatus: PropTypes.string,
  statusMessage: PropTypes.string,
};

UsuarioDialog.defaultProps = {
  dialogType: "add",
  selectedUsuario: null,
  currentUser: null,
  isSubmitting: false,
  submitStatus: null,
  statusMessage: "",
};

export default UsuarioDialog;
